import api, { normalizeList } from './api';

// Flash Report API Functions

// 1. Create Flash Report
export const createFlashReport = async (data) => {
    try {
        const response = await api.post('/api/service-orders/flash-reports/', data);
        return response.data;
    } catch (error) {
        console.error('Error creating flash report:', error);
        throw error;
    }
};

// 2. Get All Flash Reports
export const getAllFlashReports = async () => {
    try {
        const response = await api.get('/api/service-orders/flash-reports/');
        return normalizeList(response.data);
    } catch (error) {
        console.error('Error fetching all flash reports:', error);
        throw error;
    }
};

// 3. Get Specific Flash Report
export const getFlashReport = async (id) => {
    try {
        const response = await api.get(`/api/service-orders/flash-reports/${id}/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching flash report:', error);
        throw error;
    }
};

// 4. Update Flash Report
export const updateFlashReport = async (id, data) => {
    try {
        const response = await api.put(`/api/service-orders/flash-reports/${id}/`, data);
        return response.data;
    } catch (error) {
        console.error('Error updating flash report:', error);
        throw error;
    }
};

// JSON (no files) update via PATCH
export const patchFlashReport = async (id, data) => {
    try {
        const response = await api.patch(`/api/service-orders/flash-reports/${id}/`, data);
        return response.data;
    } catch (error) {
        console.error('Error patching flash report:', error);
        throw error;
    }
};

// 5. Delete Flash Report
export const deleteFlashReport = async (id) => {
    try {
        await api.delete(`/api/service-orders/flash-reports/${id}/`);
        return true;
    } catch (error) {
        console.error('Error deleting flash report:', error);
        throw error;
    }
};

// 6. Add Observation to Flash Report
export const addObservation = async (id, observationData) => {
    try {
        const response = await api.post(`/api/service-orders/flash-reports/${id}/add_observation/`, observationData);
        return response.data;
    } catch (error) {
        console.error('Error adding observation:', error);
        throw error;
    }
};

// Upload images for a FlashReport observation
export const uploadFlashObservationImages = async (id, observationId, files = []) => {
    try {
        const fd = new FormData();
        fd.append('observation_id', observationId);
        (Array.isArray(files) ? files : [files]).forEach((f) => {
            if (f) fd.append('images', f);
        });
        const response = await api.post(`/api/service-orders/flash-reports/${id}/upload_observation_images/`, fd);
        return response.data;
    } catch (error) {
        console.error('Error uploading observation images:', error);
        throw error;
    }
};

// 7. Add Note to Flash Report
export const addNote = async (id, noteData) => {
    try {
        const response = await api.post(`/api/service-orders/flash-reports/${id}/add_note/`, noteData);
        return response.data;
    } catch (error) {
        console.error('Error adding note:', error);
        throw error;
    }
};

// 8. Bump Flash Report Revision
// (Removed) bumpFlashReportRevision - handled on frontend display only

// PDF download functionality removed - disabled

// 9. Get Flash Reports by Service ID and Company ID
export const getFlashReportsByServiceAndCompany = async (serviceId, companyId) => {
    try {
        const response = await api.get('/api/service-orders/flash-reports/by_service_and_company/', {
            params: {
                service_id: serviceId,
                company_id: companyId
            }
        });
        return normalizeList(response.data);
    } catch (error) {
        console.error('Error fetching flash reports by service and company:', error);
        throw error;
    }
};

// Upload PDF for Flash Report
export const uploadFlashReportPdf = async (id, pdfBlob) => {
    try {
        const formData = new FormData();
        formData.append('pdf', pdfBlob, `flash_report_${id}.pdf`);
        // Use longer timeout (3 minutes) for PDF uploads as S3 uploads can take time
        const response = await api.post(`/api/service-orders/flash-reports/${id}/upload-pdf/`, formData, {
            timeout: 180000 // 3 minutes
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading flash report PDF:', error);
        throw error;
    }
};

// Flash Report Service Object
export const reportService = {
    createFlashReport: createFlashReport,
    getAll: getAllFlashReports,
    getById: getFlashReport,
    update: updateFlashReport,
    patch: patchFlashReport,
    delete: deleteFlashReport,
    addObservation: addObservation,
    uploadObservationImages: uploadFlashObservationImages,
    addNote: addNote,
    uploadPdf: uploadFlashReportPdf,
    getByServiceAndCompany: getFlashReportsByServiceAndCompany
};

// DetailsReport API Functions

// 1. Get Available Report Types
export const getDetailsReportTypes = async () => {
    try {
        const response = await api.get('/api/service-orders/details-reports/report_types/');
        return response.data;
    } catch (error) {
        console.error('Error fetching report types:', error);
        throw error;
    }
};

// 2. Get All Details Reports
export const getAllDetailsReports = async () => {
    try {
        const response = await api.get('/api/service-orders/details-reports/');
        return response.data;
    } catch (error) {
        console.error('Error fetching all details reports:', error);
        throw error;
    }
};

// 3. Get Details Report by ID
export const getDetailsReport = async (id) => {
    try {
        const response = await api.get(`/api/service-orders/details-reports/${id}/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching details report:', error);
        throw error;
    }
};

// 4. Create Details Report
export const createDetailsReport = async (data) => {
    try {
        const response = await api.post('/api/service-orders/details-reports/', data);
        return response.data;
    } catch (error) {
        console.error('Error creating details report:', error);
        throw error;
    }
};

// 5. Update Details Report
export const updateDetailsReport = async (id, data) => {
    try {
        const response = await api.put(`/api/service-orders/details-reports/${id}/`, data);
        return response.data;
    } catch (error) {
        console.error('Error updating details report:', error);
        throw error;
    }
};

// 6. Partial Update Details Report
export const patchDetailsReport = async (id, data) => {
    try {
        const response = await api.patch(`/api/service-orders/details-reports/${id}/`, data);
        return response.data;
    } catch (error) {
        console.error('Error patching details report:', error);
        throw error;
    }
};

// 7. Delete Details Report
export const deleteDetailsReport = async (id) => {
    try {
        await api.delete(`/api/service-orders/details-reports/${id}/`);
        return true;
    } catch (error) {
        console.error('Error deleting details report:', error);
        throw error;
    }
};

// 8. Filter Reports by Type
export const getDetailsReportsByType = async (reportType) => {
    try {
        const response = await api.get('/api/service-orders/details-reports/by_report_type/', {
            params: { report_type: reportType }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching reports by type:', error);
        throw error;
    }
};

// 9. Filter Reports by Service ID
export const getDetailsReportsByServiceId = async (serviceId) => {
    try {
        const response = await api.get('/api/service-orders/details-reports/by_service_id/', {
            params: { service_id: serviceId }
        });
        return normalizeList(response.data);
    } catch (error) {
        console.error('Error fetching reports by service ID:', error);
        throw error;
    }
};

// 10. Filter Reports by Customer ID
export const getDetailsReportsByCustomerId = async (customerId) => {
    try {
        const response = await api.get('/api/service-orders/details-reports/by_customer_id/', {
            params: { customer_id: customerId }
        });
        return normalizeList(response.data);
    } catch (error) {
        console.error('Error fetching reports by customer ID:', error);
        throw error;
    }
};

// 11. Filter Reports by Service ID and Company ID
export const getDetailsReportsByServiceAndCompany = async (serviceId, companyId) => {
    try {
        const response = await api.get('/api/service-orders/details-reports/by_service_and_company/', {
            params: {
                service_id: serviceId,
                company_id: companyId
            }
        });
        return normalizeList(response.data);
    } catch (error) {
        console.error('Error fetching reports by service and company:', error);
        throw error;
    }
};

// 12. Filter Reports by Service ID, Company ID, and Report Type
export const getDetailsReportsByServiceCompanyAndType = async (serviceId, companyId, reportType) => {
    try {
        const response = await api.get('/api/service-orders/details-reports/by_service_company_and_type/', {
            params: {
                service_id: serviceId,
                company_id: companyId,
                report_type: reportType
            }
        });
        return normalizeList(response.data);
    } catch (error) {
        console.error('Error fetching reports by service, company and type:', error);
        throw error;
    }
};

// Check if completed flash report exists
export const checkFlashReportCompleted = async (serviceOrderId, companyId) => {
    try {
        const response = await api.get('/api/service-orders/flash-reports/check_completed_exists/', {
            params: {
                service_order_id: serviceOrderId,
                company_id: companyId
            }
        });
        return response.data.has_completed;
    } catch (error) {
        console.error('Error checking flash report completion:', error);
        return false;
    }
};

// Get latest flash report version for editing
export const getLatestFlashReportForEditing = async (serviceOrderId, companyId) => {
    try {
        const response = await api.get('/api/service-orders/flash-reports/get_latest_for_editing/', {
            params: {
                service_order_id: serviceOrderId,
                company_id: companyId
            }
        });
        // The response.data is the report object directly, not wrapped in {report: ...}
        const report = response.data.report || response.data;
        return report || null;
    } catch (error) {
        console.error('Error getting latest flash report:', error);
        return null;
    }
};

// Get preview report_no for a new details report
export const getPreviewReportNo = async (reportType, serviceOrderId) => {
    try {
        const response = await api.get('/api/service-orders/details-reports/preview-report-no/', {
            params: {
                report_type: reportType,
                service_order_id: serviceOrderId
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error getting preview report_no:', error);
        return null;
    }
};

// Check if completed details report exists
export const checkDetailsReportCompleted = async (serviceId, companyId, reportType) => {
    try {
        const response = await api.get('/api/service-orders/details-reports/check_completed_exists/', {
            params: {
                service_id: serviceId,
                company_id: companyId,
                report_type: reportType
            }
        });
        return response.data.has_completed;
    } catch (error) {
        console.error('Error checking details report completion:', error);
        return false;
    }
};

// Get latest details report version for editing
export const getLatestDetailsReportForEditing = async (serviceId, companyId, reportType) => {
    try {
        const response = await api.get('/api/service-orders/details-reports/get_latest_for_editing/', {
            params: {
                service_id: serviceId,
                company_id: companyId,
                report_type: reportType
            }
        });
        // The response.data is the report object directly, not wrapped in {report: ...}
        const report = response.data.report || response.data;
        return report || null;
    } catch (error) {
        console.error('Error getting latest details report:', error);
        return null;
    }
};

// 13. Add Observation to Report
export const addDetailsReportObservation = async (id, observationData) => {
    try {
        const response = await api.post(`/api/service-orders/details-reports/${id}/add_observation/`, observationData);
        return response.data;
    } catch (error) {
        console.error('Error adding observation to details report:', error);
        throw error;
    }
};

// Upload images for a DetailsReport observation
export const uploadDetailsObservationImages = async (id, observationId, files = []) => {
    try {
        const fd = new FormData();
        fd.append('observation_id', observationId);
        (Array.isArray(files) ? files : [files]).forEach((f) => {
            if (f) fd.append('images', f);
        });
        const response = await api.post(`/api/service-orders/details-reports/${id}/upload_observation_images/`, fd);
        return response.data;
    } catch (error) {
        console.error('Error uploading details observation images:', error);
        throw error;
    }
};

// 14. Add Probe Detail to Report
export const addDetailsReportProbeDetail = async (id, probeData) => {
    try {
        const response = await api.post(`/api/service-orders/details-reports/${id}/add_probe_detail/`, probeData);
        return response.data;
    } catch (error) {
        console.error('Error adding probe detail to details report:', error);
        throw error;
    }
};

// Upload RT sketch figure image for Details Report
export const uploadDetailsReportRtSketchImage = async (id, file) => {
    try {
        const fd = new FormData();
        fd.append('rt_exposure_sketch_image', file);
        const response = await api.post(`/api/service-orders/details-reports/${id}/upload-rt-sketch-image/`, fd);
        return response.data;
    } catch (error) {
        console.error('Error uploading RT sketch image:', error);
        throw error;
    }
};

// Upload PDF for Details Report
export const uploadDetailsReportPdf = async (id, pdfBlob) => {
    try {
        const formData = new FormData();
        formData.append('pdf', pdfBlob, `details_report_${id}.pdf`);
        // Use longer timeout (3 minutes) for PDF uploads as S3 uploads can take time
        const response = await api.post(`/api/service-orders/details-reports/${id}/upload-pdf/`, formData, {
            timeout: 180000 // 3 minutes
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading details report PDF:', error);
        throw error;
    }
};

// Get email recipient for Details Report
export const getDetailsReportEmailRecipient = async (id) => {
    try {
        const response = await api.get(`/api/service-orders/details-reports/${id}/email-recipient/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching details report email recipient:', error);
        throw error;
    }
};

// Send Details Report by Email
export const sendDetailsReportEmail = async (id, payload = {}) => {
    try {
        const response = await api.post(`/api/service-orders/details-reports/${id}/send-email/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error sending details report email:', error);
        throw error;
    }
};

// DetailsReport Service Object
export const detailsReportService = {
    getReportTypes: getDetailsReportTypes,
    getAll: getAllDetailsReports,
    getById: getDetailsReport,
    create: createDetailsReport,
    update: updateDetailsReport,
    patch: patchDetailsReport,
    delete: deleteDetailsReport,
    getByType: getDetailsReportsByType,
    getByServiceId: getDetailsReportsByServiceId,
    getByCustomerId: getDetailsReportsByCustomerId,
    getByServiceAndCompany: getDetailsReportsByServiceAndCompany,
    getByServiceCompanyAndType: getDetailsReportsByServiceCompanyAndType,
    addObservation: addDetailsReportObservation,
    addProbeDetail: addDetailsReportProbeDetail,
    uploadObservationImages: uploadDetailsObservationImages,
    uploadRtSketchImage: uploadDetailsReportRtSketchImage,
    uploadPdf: uploadDetailsReportPdf,
    getEmailRecipient: getDetailsReportEmailRecipient,
    sendEmail: sendDetailsReportEmail,
};

/*
DetailsReport API Documentation

Overview
--------
The DetailsReport API provides endpoints for managing detailed inspection reports for various NDE (Non-Destructive Examination) methods including UT Weld, MT Yoke, MT Bench, PT Visible, PT Fluorescent, VT Weld, and Visual Examination.

Base URL
--------
http://localhost:8000/api/service-orders

Authentication
--------------
All endpoints require authentication. Include the authorization header:
Authorization: Bearer YOUR_TOKEN_HERE

Report Types
-----------
The following report types are supported:
- UT Weld - Ultrasonic Testing for Welds
- MT Yoke - Magnetic Particle Testing with Yoke
- MT Bench - Magnetic Particle Testing on Bench
- PT Visible - Penetrant Testing (Visible)
- PT Fluorescent - Penetrant Testing (Fluorescent)
- VT Weld - Visual Testing for Welds
- Visual Examination - General Visual Examination

Endpoints
---------

1. Get Available Report Types
   GET /details-reports/report_types/
   Returns all available report types.

2. List All Details Reports
   GET /details-reports/
   Returns all details reports (filtered by user permissions).

3. Get Details Report by ID
   GET /details-reports/{id}/
   Returns a specific details report by ID.

4. Create Details Report
   POST /details-reports/
   Creates a new details report.

5. Update Details Report
   PUT /details-reports/{id}/
   Updates an existing details report.

6. Partial Update Details Report
   PATCH /details-reports/{id}/
   Partially updates an existing details report.

7. Delete Details Report
   DELETE /details-reports/{id}/
   Deletes a details report.

8. Filter Reports by Type
   GET /details-reports/by_report_type/?report_type={report_type}
   Returns reports filtered by report type.

9. Filter Reports by Service ID
   GET /details-reports/by_service_id/?service_id={service_id}
   Returns reports filtered by service ID.

10. Filter Reports by Customer ID
    GET /details-reports/by_customer_id/?customer_id={customer_id}
    Returns reports filtered by customer ID.

11. Filter Reports by Service ID and Customer ID
    GET /details-reports/by_service_and_customer/?service_id={service_id}&customer_id={customer_id}
    Returns reports filtered by both service ID and customer ID.

12. Filter Reports by Service ID, Customer ID, and Report Type
    GET /details-reports/by_service_customer_and_type/?service_id={service_id}&customer_id={customer_id}&report_type={report_type}
    Returns reports filtered by service ID, customer ID, and report type.

13. Add Observation to Report
    POST /details-reports/{id}/add_observation/
    Adds a new observation to an existing report.

14. Add Probe Detail to Report
    POST /details-reports/{id}/add_probe_detail/
    Adds a new probe detail to an existing report.

Field Descriptions
-----------------

Common Fields (All Report Types):
- service_id: Service ID associated with the report
- customer_id: Customer ID associated with the report
- report_type: Type of report (required)
- client: Client name
- report_no: Report number
- address: Address
- revision: Revision number
- report_date: Report date (YYYY-MM-DD format)
- test_date: Test date (YYYY-MM-DD format)
- material: Material being tested
- drawing_no: Drawing number
- stage: Stage of testing
- surface_condition: Surface condition
- technique_method: Technique/method used
- thermometer: Thermometer used
- examination_extent: Extent of examination
- specification: Specification followed
- acceptance_standard: Acceptance standard
- remark: Remarks
- note: Notes

Personnel Fields:
- performed_by_name: Name of person who performed the test
- performed_by_date: Date when test was performed
- performed_by_cert: Certification of person who performed the test
- reviewed_by_name: Name of person who reviewed the test
- reviewed_by_date: Date when test was reviewed
- reviewed_by_cert: Certification of person who reviewed the test
- witnessed_by_name: Name of person who witnessed the test
- witnessed_by_date: Date when test was witnessed
- witnessed_by_cert: Certification of person who witnessed the test

UT Weld Specific Fields:
- ufd_and_sr_no: UFD and SR number
- calibration_validity: Calibration validity date
- calibration_standard: Calibration standard used
- reference_block: Reference block used
- cable_details: Cable details
- couplant_details: Couplant details
- cleaner: Cleaner used
- sensitivity_calibration_method: Sensitivity calibration method
- reference_block_identification: Reference block identification
- test_range: Test range
- sensitivity_with_required_gain: Sensitivity with required gain
- part_or_block_temperature: Part or block temperature

MT Yoke/MT Bench Specific Fields:
- magnetic_particle_medium: Magnetic particle medium
- contrast_paint: Contrast paint used
- bath_medium: Bath medium
- test_equipment_used: Test equipment used
- current_type_pole_spacing: Current type and pole spacing
- application_bath_medium: Application of Bath
- concentration_bath: Concentration of bath
- lighting_equipment: Lighting equipment used
- light_intensity: Light intensity
- uv_meter: UV meter used
- lux_meter: Lux meter used
- serial_calibration_date: Serial calibration date
- magnetisation_method: Magnetisation method
- method_bath_application: Method of bath application
- bath_powder_contamination_check: Bath powder contamination check
- part_surface_temperature: Part surface temperature
- field_adequacy_ensured_by: Field adequacy ensured by
- background_white_light_intensity: Background white light intensity
- field_strength_gauss_meter: Field strength gauss meter
- gauss_meter: Gauss meter used

PT Visible/PT Fluorescent Specific Fields:
- penetrant: Penetrant used
- developer: Developer used
- precleaning: Precleaning method
- drying_time_after_precleaning: Drying time after precleaning
- dwell_time: Dwell time
- penetrant_application_method: Penetrant application method
- removal_of_excess_penetrant: Removal of excess penetrant
- drying_time_before_developer: Drying time before developer
- developing_and_interpretation_time: Developing and interpretation time
- developer_application_method: Developer application method
- post_cleaning: Post cleaning method
- uv_light: UV light used

VT Weld/Visual Examination Specific Fields:
- equipment_used: Equipment used
- surface_preparation: Surface preparation
- cracks: Cracks observed
- weld_base_metal_fusion: Weld base metal fusion
- weld_profile: Weld profile
- undercut: Undercut observed
- reinforcement: Reinforcement
- surface_irregularities: Surface irregularities
- spatter: Spatter observed
- porosity: Porosity observed
- undersize_weld: Undersize weld
- crater_cross_section: Crater cross section
- hot_tears_and_cracks: Hot tears and cracks
- shrinkage: Shrinkage observed
- gas_porosity: Gas porosity
- rat_tails: Rat tails
- veining: Veining
- sand_inclusions: Sand inclusions
- wrinkles_laps_folds_cold_shuts: Wrinkles, laps, folds, cold shuts
- cutting_marks: Cutting marks
- chaplets: Chaplets
- surface_roughness: Surface roughness
- weld_repair_area: Weld repair area

Observation Fields:
- description: Description of observation
- join_qty: Join quantity (UT Weld, VT Weld)
- job_qty: Job quantity (UT Weld, VT Weld)
- quantiy: Quantity (MT Yoke, MT Bench, PT Visible, PT Fluorescent, Visual Examination)
- observation: Observation details
- evaluation: Evaluation of observation

Probe Detail Fields:
- tr_type: Transducer type
- probe_id: Probe ID
- make: Make of probe
- specification: Probe specification

Error Responses:
- 400 Bad Request: Field validation errors
- 401 Unauthorized: Authentication required
- 403 Forbidden: Permission denied
- 404 Not Found: Resource not found
- 500 Internal Server Error: Server error

Notes:
1. All fields except report_type are optional.
2. The API automatically handles nested objects (observations and probe_details) during create and update operations.
3. Users can only see reports they created (unless they are staff/superuser).
4. All timestamps are in ISO 8601 format.
5. Date fields should be in YYYY-MM-DD format.
*/